import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { ICampaignGroup } from '../types';

import { useCampaignGroupQuery } from '~/api/graphql/queries/campaignGroup.generated';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHandleNavigate } from '~/hooks/useHandleNavigate';
import { CAMPAIGN_MSG } from '~/modules/campaign/constants';
import { deepCopy, sortByConditions } from '~/utils';
import { useCampaignGroupStore } from '../stores';
import { INavigateKey } from '~/types/navigate';
import { ROUTE_PATH } from '~/config/navigate';
import { useToast } from '~/hooks/useToast';

export interface InitializeCampaignGroupProps {
  campaignGroupId?: ICampaignGroup['id'];
  campaignGroup?: ICampaignGroup;
}

export const useInitializeCampaignGroup = (props: InitializeCampaignGroupProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [campaignGroup, setCampaignGroup] = useState<ICampaignGroup | undefined>(
    props.campaignGroup,
  );
  const [isQueryByCampaignGroupByIdId, setIsQueryByCampaignGroupId] = useState(false);

  const { handleNavigate } = useHandleNavigate();
  const { shopifyToast } = useToast();

  const dataCampaignGroups = useCampaignGroupStore((state) => state.campaignGroups);
  const upsertCampaignGroup = useCampaignGroupStore((state) => state.upsertCampaignGroup);

  const dataCampaignGroup = useMemo(() => {
    if (!props.campaignGroupId) return;

    return dataCampaignGroups.find((c) => c.id === props.campaignGroupId);
  }, [dataCampaignGroups, props.campaignGroupId]);

  const campaignGroupCampaigns = useMemo(() => {
    if (!campaignGroup) return [];

    const dataCampaigns = campaignGroup.campaigns || [];
    const dataCampaignTemplates: ICampaignTemplate[] = dataCampaigns.filter(
      (item) => !item.deletedAt,
    );
    return sortByConditions<ICampaignTemplate>(dataCampaignTemplates, [
      {
        attr: 'createdAt',
        order: 'desc',
      },
    ]);
  }, [campaignGroup]);

  const handleGetCampaignGroup = useCallback(() => {
    return campaignGroup;
  }, [campaignGroup]);

  const handleSetCampaignGroup = useCallback((data?: ICampaignGroup) => {
    setCampaignGroup((prev) => {
      if (!prev) return data;
      return {
        ...prev,
        ...data,
        campaigns: data?.campaigns,
      } as ICampaignGroup;
    });
  }, []);
  const handleSetIsEditing = useCallback((isEditing: boolean) => {
    setIsEditing(isEditing);
  }, []);

  const handleDiscardCampaignGroup = useCallback(() => {
    if (!dataCampaignGroup) return;

    setCampaignGroup(deepCopy(dataCampaignGroup));
  }, [dataCampaignGroup]);

  useCampaignGroupQuery(
    { id: props.campaignGroupId ?? '' },
    {
      enabled: !!props.campaignGroupId && isQueryByCampaignGroupByIdId,
      onSuccess: (data) => {
        setIsQueryByCampaignGroupId(false);
        if (!data.campaignGroup) return;

        handleSetCampaignGroup(data.campaignGroup as ICampaignGroup);
        upsertCampaignGroup(data.campaignGroup as ICampaignGroup);
      },
      onError: () => {
        handleNavigate(ROUTE_PATH[INavigateKey.Campaigns]);
        shopifyToast(CAMPAIGN_MSG.QUERY.FAILED, { isError: true });
      },
    },
  );

  useEffect(() => {
    if (campaignGroup) return;
    if (!dataCampaignGroup) return;

    handleSetCampaignGroup(deepCopy(dataCampaignGroup));
  }, [campaignGroup, dataCampaignGroup, handleSetCampaignGroup]);

  useEffect(() => {
    if (campaignGroup) return;
    if (dataCampaignGroup) return;
    if (isQueryByCampaignGroupByIdId) return;

    setIsQueryByCampaignGroupId(true);
  }, [campaignGroup, dataCampaignGroup, isQueryByCampaignGroupByIdId]);

  return {
    campaignGroup,
    campaignGroupCampaigns,
    isQueryByCampaignGroupByIdId,
    handleSetCampaignGroup,
    handleGetCampaignGroup,
    handleDiscardCampaignGroup,
    isEditing,
    handleSetIsEditing,
  };
};

export type IInitializeCampaignGroup = ReturnType<typeof useInitializeCampaignGroup>;
